import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import Button from "../UI/Button";
import { createExperience } from "../../utils/APIs";
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { useUserContext } from "../../contexts/UserContext";
import Loader from "../UI/Loader/Loader";
import { ExperienceContext } from "../../contexts/ExperienceContext";
import Papa from "papaparse";
import SpinLoader from "../UI/Loader/SpinLoader";
import Dropdown from "../UI/Dropdown/Dropdown";
import { formatCSVData, isValidJsonString, updateHtmlTemplate } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Palette, XCircle, EllipsisVertical, Download, Upload, Plus, Trash2, Copy, Check } from "lucide-react";
import { toast } from "react-toastify";
import EditIcon from "../../assets/icons/edit.svg";
import NewWindowIcon from '../../assets/icons/new-window.svg';
import ArrayIcon from '../../assets/icons/array.svg';
import ManageEditInputModal from "./ManageEditInputModal";
import useComponentVisible from "../UI/ComponentVisible";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import AgGridWrapper from "../UI/AgGrid";

const CSV_DATA_STATUS = {
    error: "",
    success: "",
};

const AddExperienceModal = ({ showAddExperiencesModal, handleCloseModal, fetchOrgUsage, usageCount }) => {
    const fileInputRef = useRef("");
    const { enums, content } = useContentfulContext();

    const { userData } = useUserContext();
    const { getAllExperiencesHandler, rawData, getTemplatesRawData } =
        useContext(ExperienceContext);
    const navigator = useNavigate();
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [validateCsvLoading, setValidateCsvLoading] = useState(false);
    const [csvValidation, setCsvValidation] = useState(CSV_DATA_STATUS);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [inputTypes, setInputTypes] = useState({});
    const [activeFilter, setActiveFilter] = useState(["Local", "Global"]);
    const [errors, setErrors] = useState({});
    const [rowData, setRowData] = useState([]);
    const [showEditor, setShowEditor] = useState(false);
    const [selectedCellToEdit, setSelectedCellToEdit] = useState("");
    const [selectedRowData, setSelectedRowData] = useState({});
    const [deleteIds, setDeleteIds] = useState([]);

    // FIXED: Clear all validation states when template changes
    const clearAllStates = () => {
        setError("");
        setCsvValidation(CSV_DATA_STATUS);
        setErrors({});
        setRowData([]);
        setDeleteIds([]);
        setSelectedCellToEdit("");
        setSelectedRowData({});
        setIsComponentVisible(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (!selectedTemplate) return;

        const temp = JSON.parse(selectedTemplate?.config_json).reduce(
            (map, item) => {
                if (item.input && item.input.input_label) {
                    map[item.input.input_label] = item;
                }
                return map;
            },
            {}
        );
        setInputTypes(temp);
    }, [selectedTemplate]);

    const handleFilterChange = (filterType) => {
        setActiveFilter((prevState) => {
            if (prevState.includes(filterType)) {
                return prevState.filter((item) => item !== filterType);
            } else {
                return [...prevState, filterType];
            }
        });
    };

    const filterButtons = (type) => {
        const isActive = activeFilter.includes(type);
        return (
            <button
                type="button"
                onClick={() => handleFilterChange(type)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all border cursor-pointer ${
                    isActive
                        ? "bg-primary text-white border-primary shadow-xs"
                        : "bg-white text-stride-muted border-border hover:bg-surface-alt hover:text-stride-body"
                }`}
            >
                {isActive && <Check size={12} className="stroke-[2.5]" />}
                <span>{type}</span>
            </button>
        );
    };

    const filteredData = rawData.filter((item) =>
        activeFilter.some(
            (filter) => filter.toLowerCase() === item.scope.toLowerCase()
        )
    );

    useEffect(() => {
        // clear selected template if it doesn't exist in filtered data
        if (
            selectedTemplate &&
            !filteredData.find((item) => item._id === selectedTemplate._id)
        ) {
            clearAllStates();
            // setSelectedTemplate(null);
        }
    }, [filteredData])
    

    const reformJson = (flatData) => {
        const result = {};
        for (const key in flatData) {
            const value = flatData[key];
            const keys = key.split(/[\.\[\]]/).filter((k) => k); // Split by dot and brackets

            keys.reduce((acc, k, i) => {
                if (i === keys.length - 1) {
                    acc[k] = value == false ? false : value || "";
                } else {
                    if (!acc[k]) {
                        acc[k] = isNaN(keys[i + 1]) ? {} : [];
                    }
                    return acc[k];
                }
            }, result);
        }
        return result;
    };

    // FIXED: Properly clear all states when template changes
    const handleChooseTemplate = (val) => {
        // Clear everything first
        clearAllStates();
        
        const data = JSON.parse(val?.config_json);

        let formattedObj = { "Experience Name": "Sample Experience" };
        for (const item of data) {
            formattedObj[item.input.input_label] = item?.input?.input_default_value;
        }

        setSelectedTemplate(val);
        setRowData([formattedObj]);
    };

    const downloadCsv = () => {
        const data = JSON.parse(selectedTemplate?.config_json);

        let flatData = ["Experience Name"];
        let defaultValues = ["Sample Experience"];
        let mandatory = [" "];
        let min_length = [" "];
        let max_length = [" "];
        let types = [" "];
        for (const item of data) {
            flatData.push(item.input.input_label || " ");
            const inputValue = item?.input?.input_default_value;
            defaultValues.push(inputValue);

            mandatory.push(item?.rules?.is_mandatory ? "Yes" : "NO");
            min_length.push(item?.rules?.min_length || " ");
            max_length.push(item?.rules?.max_length || " ");
            types.push(item.input.input_type || " ");
        }

        const csv = Papa.unparse([
            ["*****"],
            ["-> Stride - Bulk upload Template Name"],
            ["** Please do not delete first 17 rows. (17th row shows experience data with default values) **"],
            ["*****", " "],
            ["Template", selectedTemplate.name],
            ["*****"],
            ["** Mandatory **"],
            mandatory,
            ["** Min Length **"],
            min_length,
            ["** Max length **"],
            max_length,
            ["** Type **"],
            types,
            ["*****"],
            flatData,
            defaultValues
        ]);
        const filename = `${selectedTemplate.name}-experience.csv`;

        const link = document.createElement("a");
        const encodedURI = encodeURI(csv);
        const fixedEncodedURI = encodedURI.replaceAll('#', '%23');
        link.setAttribute(
            "href",
            "data:text/csv;charset=utf-8,%EF%BB%BF" + fixedEncodedURI
        );

        link.setAttribute("download", filename);
        link.click();
    };

    const handleSelectCSV = (event) => {
        const file = event.target.files[0];
        
        // FIXED: Clear validation states when new file is selected
        setCsvValidation(CSV_DATA_STATUS);
        setError("");
        setErrors({});
        
        if (!file) {
            setError(content.ADD_EXPERIENCE_MODAL.add_experience_modal.file_required);
            return;
        }

        let rows = []; // To store parsed rows
        let rowIndex = 0; // To track row number
        let templateName = "";
        Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            encoding: "utf-8",
            skipEmptyLines: true,
            step: function (row, parser) {
                // Skip all description rows
                if (rowIndex === 3) {
                    templateName = row.data[""]; // Extract the template name from the row data
                }
                if (rowIndex >= 14) {
                    rows.push(row.data); // Add valid rows after skipping
                } 
                rowIndex++;
            },
            complete: function (results) {
                const data = formatCSVData(rows);
                
                // FIXED: Validate template name match
                if(templateName?.trim() !== selectedTemplate?.name?.trim()) {
                    setError(content.ADD_EXPERIENCE_MODAL.errors.different_template);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setRowData([]);
                    return;
                }

                const csvKeys = Object.keys(data[0]);

                // FIXED: Validate config JSON
                if(!isValidJsonString(selectedTemplate?.config_json)) {
                    setError(content.ADD_EXPERIENCE_MODAL.errors.invalid_config_json);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setRowData([]);
                    return;
                }

                let isValidConfigJson = true;
                for (const item of JSON.parse(selectedTemplate?.config_json)) {
                    if(!csvKeys.includes(item?.input?.input_label)) {
                        isValidConfigJson = false;
                    }
                }

                if(!isValidConfigJson) {
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setError(content.ADD_EXPERIENCE_MODAL.errors.different_template);
                    setRowData([]);
                    return;
                }

                setRowData(data);
                checkValidation(data);
                setValidateCsvLoading(false);
            },
        });
    };

    function updateKeyNames(dataArray, mappingArray) {
        // Create a mapping of old keys to new keys from the mappingArray
        const keyMapping = mappingArray.reduce((map, item) => {
            if (item.input && item.input.input_label) {
                map[item.input.input_label] = item.id;
            }
            return map;
        }, {});

        // Update keys in the data array based on the mapping
        return dataArray.map((obj) => {
            const updatedObj = {};
            for (const key in obj) {
                // Replace key with mapped key if it exists, otherwise keep the original
                const newKey = keyMapping[key] || key;
                updatedObj[newKey] = obj[key];
            }
            return updatedObj;
        });
    }

    const handleSubmit = async () => {
        try {
            setError("");

            // FIXED: Validate before submission
            const errs = checkValidation(rowData);
            if (Object.values(errs).some((errObj) => Object.keys(errObj).length > 0)) {
                setError(content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error);
                return;
            }

            // FIXED: Check if there's actual data to submit (more than just header row)
            if (rowData.length <= 1) {
                setError(content.ADD_EXPERIENCE_MODAL.errors.required);
                return;
            }

            const customObj = [];
            const updatedKey = updateKeyNames(
                rowData.slice(1),
                JSON.parse(selectedTemplate.config_json)
            );

            for (let i = 0; i < updatedKey.length; i++) {
                let configJson;

                if(selectedTemplate.type === enums?.templateTypes.json_a_to_html.id) {
                    const temp = JSON.parse(JSON.stringify(updatedKey[i]));
                    delete temp["Experience Name"];

                    const updatedHtml = updateHtmlTemplate(selectedTemplate.html, temp)
                    configJson = updatedHtml;
                } else {
                    const res = reformJson(updatedKey[i]);
                    delete res["Experience Name"];
                    delete res.templates;

                    configJson = JSON.stringify(res);
                }

                const temp = {
                    name: updatedKey[i]["Experience Name"],
                    templates: selectedTemplate?.name,
                    experience_json: configJson,
                    source: enums?.experience_sources.bulk_upload.id,
                };
                customObj.push(temp);
            }

            if (customObj.length > enums?.REACT_APP_MAX_BULK_EXPERIENCE_UPLOAD_LIMIT) {
                setError(content.ADD_EXPERIENCE_MODAL.errors.over_limit_in_csv);
                setValidateCsvLoading(false);
                return;
            }

            if (!customObj.length) {
                setError(content.ADD_EXPERIENCE_MODAL.errors.required);
                return;
            }         
            setLoading(true);
            const res = await createExperience(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                { experiences: customObj },
                {
                    event_name: ANALYTICS_EVENTS.BULK_UPLOAD_EXPERIENCE,
                    metadata: { template_name: selectedTemplate?.name, experience_count: customObj.length, source: 'bulk_upload' }
                }
            );
            
            toast.success(res.message);
            handleClose();
            fetchOrgUsage();
            getAllExperiencesHandler();
        } catch (error) {
            console.log("error handleSubmit: ", error);
            if (error?.errors) {
                setErrors(error?.errors);
            }
            setCsvValidation({
                error: error?.message || content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error,
                success: ""
            });
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        clearAllStates();
        setSelectedTemplate(null);
        handleCloseModal();
    };

    useEffect(() => {
        getTemplatesRawData(true);
    }, []);

    // FIXED: Clear states when modal opens/closes
    useEffect(() => {
        if (!showAddExperiencesModal) {
            clearAllStates();
            setSelectedTemplate(null);
        }
    }, [showAddExperiencesModal]);

    // FIXED: Improved validation logic
    const checkValidation = (data) => {
        const newErrors = {};
        const dataToValidate = data.slice(1);
        const expNames = dataToValidate.map((x) => x["Experience Name"] || "");

        let isValid = true;

        dataToValidate.forEach((row, index) => {
            const rowIndex = index + 1;
            if (!newErrors[rowIndex]) newErrors[rowIndex] = {};

            // Validate Experience Name
            if(!row["Experience Name"] || row["Experience Name"]?.trim() === "") {
                newErrors[rowIndex]["Experience Name"] = `Experience name is required.`;
                isValid = false;
            } else {
                // Check for duplicates
                const count = expNames.filter((x) => x === row?.["Experience Name"]).length;
                if(count > 1) {
                    newErrors[rowIndex]["Experience Name"] = `Duplicate Experience name: ${row?.["Experience Name"]}`;
                    isValid = false;
                }
            }

            // Validate fields based on template config
            JSON.parse(selectedTemplate.config_json).forEach((field) => {
                const { rules, input } = field;
                const value = row[input.input_label];

                if (rules.is_mandatory && (value === undefined || value === null || value === "")) {
                    newErrors[rowIndex][input.input_label] = `${input.input_label} in row ${rowIndex + 1} is mandatory`;
                    isValid = false;
                }

                if (value !== undefined && value !== null && value !== "") {
                    const strValue = `${value}`;
                    
                    if (rules.min_length && strValue.length < rules.min_length) {
                        newErrors[rowIndex][input.input_label] = `Minimum length of ${input.input_label} in row ${rowIndex + 1} is ${rules.min_length}`;
                        isValid = false;
                    }

                    if (rules.max_length && strValue.length > rules.max_length) {
                        newErrors[rowIndex][input.input_label] = `Maximum length of ${input.input_label} in row ${rowIndex + 1} is ${rules.max_length}`;
                        isValid = false;
                    }
                }
            });
        });

        // FIXED: Set appropriate validation messages
        if (isValid) {
            setCsvValidation({
                error: "",
                success: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_success,
            });
        } else {
            setCsvValidation({
                error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error,
                success: "",
            });
        }

        setErrors(newErrors);
        return newErrors;
    };

    // FIXED: Improved cell validation
    const checkCellValidation = (rowIndex, colId, value) => {
        let temp = JSON.parse(JSON.stringify(errors));

        if (!temp[rowIndex]) temp[rowIndex] = {};

        if(colId === "Experience Name") {
            // Clear previous error first
            delete temp[rowIndex]["Experience Name"];
            
            if(!value || value?.trim() === "") {
                temp[rowIndex]["Experience Name"] = `Experience name is required.`;
                setErrors(temp);
                setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
                return;
            }

            // Check for duplicates
            const count = rowData.slice(1).filter((x) => (x["Experience Name"] === value)).length;
            if(count > 1) {
                temp[rowIndex]["Experience Name"] = `Duplicate Experience name: ${value}`;
                setErrors(temp);
                setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
                return;
            }
            
            setErrors(temp);
        } else {
            const configVal = JSON.parse(selectedTemplate.config_json).find((x) => x.input.input_label === colId);
            if (!configVal) return;
            
            const { rules, input } = configVal;

            // Clear previous error first
            delete temp[rowIndex][input.input_label];

            if (rules.is_mandatory && (value === undefined || value === null || value === "")) {
                temp[rowIndex][input.input_label] = `${input.input_label} in row ${rowIndex + 1} is mandatory`;
                setErrors(temp);
                setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
                return;
            }

            if (value !== undefined && value !== null && value !== "") {
                const strValue = `${value}`;
                
                if (rules.min_length && strValue.length < rules.min_length) {
                    temp[rowIndex][input.input_label] = `Minimum length of ${input.input_label} in row ${rowIndex + 1} is ${rules.min_length}`;
                    setErrors(temp);
                    setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
                    return;
                }

                if (rules.max_length && strValue.length > rules.max_length) {
                    temp[rowIndex][input.input_label] = `Maximum length of ${input.input_label} in row ${rowIndex + 1} is ${rules.max_length}`;
                    setErrors(temp);
                    setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
                    return;
                }
            }

            setErrors(temp);
        }
        
        // FIXED: Check if all errors are cleared
        setCommonErrorMessage(temp);
    }

    function setCommonErrorMessage(errs) {
        const hasError = Object.values(errs ?? errors).some(
            (errorDetails) => Object.keys(errorDetails).length > 0
        );
        
        if(!hasError) {
            setCsvValidation({ 
                error: "", 
                success: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_success 
            });
        }
    }

    const onCellClicked = (params) => {
        const { node, colDef } = params;
        const rowIndex = node.rowIndex;
        const field = colDef.field;

        if (errors[rowIndex]?.[field]) {
            setError(`Error: ${errors[rowIndex][field]}`);
        } else {
            setError("");
        }
    };

    const handleOpenThreeDotMenu = (e, params) => {
        e.preventDefault();
        e.stopPropagation();

        const buttonRect = e.currentTarget.getBoundingClientRect();
        const modalRect = e.currentTarget.closest(".modal-container").getBoundingClientRect();

        const menuPosition = {
            top: buttonRect.bottom - modalRect.top,
            left: buttonRect.left - modalRect.left - 192,
        };

        setMenuPosition(menuPosition);
        setSelectedRowData(params);
        setIsComponentVisible(!isComponentVisible);
    }

    const columnDefs = useMemo(() => {
        if (rowData.length > 0) {
            const columns = Object.keys(rowData[0]).map((key) => ({
                headerName: key.replace(/_/g, " "),
                field: key,
                sortable: true,
                filter: true,
                resizable: true,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                editable: (params) => {
                    if (params.node.rowIndex === 0) return false;
                    const key = params.column.colId;
                    switch (inputTypes[key]?.input?.input_type) {
                        case "rich-textarea":
                        case "color-picker":
                        case "array":
                            return false;
                        default:
                            return true;
                    }
                },
                cellStyle: (params) => {
                    const rowIndex = params.node.rowIndex;
                    let classes = { textAlign: "center" };
                    
                    if(errors[rowIndex]?.[key]) {
                        classes.border = "0.8px solid red";
                        classes.backgroundColor = "#ffe6e6";
                    }

                    if(params.node.rowIndex === 0) {
                        classes.pointerEvents = "none";
                        classes.opacity = 0.6;
                        classes.backgroundColor = "#f5f5f5";
                    }

                    return classes;
                },
                cellRenderer: (params) => {
                    const key = params.column.colId;
                    let icon = null;

                    if (inputTypes[key]?.input?.input_type === "radio") {
                        return (
                            <div className="w-full flex h-[100%] justify-center content-center">
                                <input 
                                    type='checkbox'
                                    checked={params.value}
                                    onChange={() => {
                                        handleModalSave(params.node.rowIndex, key, !params.value);
                                    }}
                                />
                            </div>
                        )
                    }

                    switch (inputTypes[key]?.input?.input_type) {
                        case "color-picker":
                            icon = (
                                <Palette
                                    className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                    onClick={() => {
                                        setShowEditor(true);
                                        setSelectedCellToEdit({
                                            ...inputTypes[key],
                                            value: params.value,
                                            rowIndex: params.node.rowIndex,
                                            colId: key,
                                        });
                                    }}
                                />
                            );
                            break;
                        case "rich-textarea":
                            icon = (
                                <img
                                    src={EditIcon}
                                    alt="String"
                                    className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                    onClick={() => {
                                        setShowEditor(true);
                                        setSelectedCellToEdit({
                                            ...inputTypes[key],
                                            value: params.value,
                                            rowIndex: params.node.rowIndex,
                                            colId: key,
                                        });
                                    }}
                                />
                            );
                            break;
                        case "url-textarea":
                            icon = (
                                <img
                                    src={NewWindowIcon}
                                    alt="String"
                                    className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                    onClick={() => {
                                        if (params.value) {
                                            window.open(params.value, "_blank");
                                        }
                                    }}
                                />
                            );
                            break;
                        case "array":
                            if(typeof params?.value === "string") {
                                handleModalSave(params.node.rowIndex, key, params.value?.split(","));
                            }
                            icon = (
                                <img
                                    src={ArrayIcon}
                                    alt="String"
                                    className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                    onClick={() => {
                                        setShowEditor(true);
                                        setSelectedCellToEdit({
                                            ...inputTypes[key],
                                            value: typeof params?.value === "string" ? params?.value?.split(",") : params.value,
                                            rowIndex: params.node.rowIndex,
                                            colId: key,
                                        });
                                    }}
                                />
                            )
                            break;
                        default:
                            break;
                    }

                    return (
                        <div id={params.rowIndex === 1 ? "edit-cell-id" : ""} className="flex items-center justify-between w-full">
                            <p
                                title={params.value}
                                className={`${
                                    icon && "max-w-[calc(100%-30px)]"
                                } truncate`}
                            >
                                {typeof params.value === "object" ? params?.value?.join(",") : params.value}
                            </p>
                            {icon}
                        </div>
                    );
                },
            }));

            columns.push({
                headerName: "",
                field: "actions",
                maxWidth: 50,
                pinned: "right",
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: (params) => {
                    return (
                        <div 
                            id={params?.rowIndex === 0 ? "first-row-action-of-preview-id" : ""}
                            onClick={(e) => handleOpenThreeDotMenu(e, params)}
                            className="flex justify-center items-center h-full w-full cursor-pointer text-stride-muted hover:text-stride-heading transition-colors"
                        >
                            <EllipsisVertical 
                                size={16}
                                className="cursor-pointer"
                            />
                        </div>
                    );
                },
            });

            columns.unshift({
                headerName: "Status",
                field: "status",
                maxWidth: 90,
                sortable: false,
                pinned: "left",
                filter: false,
                resizable: true,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                headerClass: "center-header ag-align-center-header w-full",
                cellStyle: { textAlign: "center" },
                cellRenderer: (params) => {
                    const rowIndex = params.node.rowIndex;
                    const hasError =
                        errors[rowIndex] &&
                        Object.keys(errors[rowIndex]).length > 0;
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            {hasError ? (
                                <XCircle color="red" size={24} />
                            ) : (
                                <CheckCircle color="green" size={24} />
                            )}
                        </div>
                    );
                },
            });

            columns.unshift({
                headerName: "#",
                field: "rowNumber",
                maxWidth: 45,
                pinned: "left",
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" },
                valueGetter: (params) => params.node.rowIndex + 1,
            });

            columns.unshift({
                headerName: "",
                field: "checkbox",
                maxWidth: 35,
                pinned: "left",
                checkboxSelection: (params) => params.node.rowIndex !== 0,
                headerCheckboxSelection: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" },
            });

            return columns;
        }
        return [];
    }, [rowData, errors]);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitCellContents",
        };
    }, [rowData]);

    const onCellValueChanged = (event) => {
        const { data, rowIndex } = event;
        setRowData((prevData) => {
            const updatedData = [...prevData];
            updatedData[rowIndex] = data;
            return updatedData;
        });
    };

    const onCellEditingStopped = (event) => {
        setError("");
        checkCellValidation(event.rowIndex, event.column.colId, event.value);
    };

    const onSortChanged = (params) => {
        const sortedData = [];
        params.api.forEachNodeAfterFilterAndSort((node) => {
            sortedData.push(node.data);
        });

        checkValidation(sortedData);
        setRowData(sortedData);
    };

    const handleModalSave = (rowIndex, colId, newVal) => {
        onCellValueChanged({
            rowIndex,
            data: { ...rowData[rowIndex], [colId]: newVal },
        });

        checkCellValidation(rowIndex, colId, newVal);
        setShowEditor(false);
    };

    const handleHideEditModal = () => {
        setShowEditor(false);
        setSelectedCellToEdit("");
    };

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    };

    // FIXED: Validate after adding duplicate row
    const handleAddDuplicateRow = () => {
        const newRow = { ...rowData[selectedRowData.rowIndex], "Experience Name": `Copy ${selectedRowData.data["Experience Name"]}` };
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newRow);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    };

    // FIXED: Validate after adding new row
    const handleAddRow = () => {
        const updatedData = [...rowData];
        const newItem = {
            ...Object.keys(selectedRowData.data).reduce((acc, key) => {
                acc[key] = null; // Set all fields to null
                return acc;
            }, {}),
            "Experience Name": `Copy ${selectedRowData.data["Experience Name"]}` // Only `name` is prefixed
        };
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newItem);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    // FIXED: Validate after deleting rows
    const handleDeleteRow = () => {
        if(deleteIds.length > 0) {
            const temp = deleteIds.filter((x) => x !== 0);
            const indexesSet = new Set(temp);
            const updatedData = rowData.filter((_, index) => !indexesSet.has(index));
            setRowData(updatedData);
            setIsComponentVisible(false);
            setDeleteIds([]);
            checkValidation(updatedData);
            return;
        }
        
        if(selectedRowData.rowIndex === 0) return;
        
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex, 1);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    const handleOnRowSelected = (event) => {
        const selectedNodes = event.api.getSelectedNodes();
        const indexes = selectedNodes.map((node) => node.rowIndex);
        setDeleteIds(indexes);
    }

    return (
        <>
            {showAddExperiencesModal && (
                <PrimaryModal
                    title={content.ADD_EXPERIENCE_MODAL.add_experience_modal.title}
                    hideModal={handleClose}
                    widthClass="max-w-6xl"
                >
                    {loading && <Loader />}
                    {validateCsvLoading && (
                        <div className="rounded-lg absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                            <SpinLoader />
                        </div>
                    )}
                    {!loading && rawData?.length === 0 && (
                        <div className="pt-6 space-y-6 pb-10">
                            <div className="text-center flex flex-col items-center content-center">
                                <p className="text-xl leading-tight tracking-tight text-black text-center my-4">
                                    {
                                        content.ADD_EXPERIENCE_MODAL
                                            .add_experience_modal
                                            .no_template_found
                                    }
                                </p>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        navigator(
                                            "/experience/create-template"
                                        );
                                    }}
                                >
                                    {
                                        content.ADD_EXPERIENCE_MODAL
                                            .add_experience_modal
                                            .create_new_template
                                    }
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-3 px-6 pt-5">
                        <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider min-w-28">
                            Select Scope
                        </span>
                        <div className="flex gap-2">
                            {filterButtons("Local")}
                            {filterButtons("Global")}
                        </div>
                    </div>

                    {rawData?.length > 0 && (
                        <div className="p-6 space-y-5">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-surface-subtle border border-border">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <span className="text-xs font-semibold text-stride-heading min-w-max">
                                        {content.ADD_EXPERIENCE_MODAL.add_experience_modal.template_based_upload}
                                    </span>
                                    <Dropdown
                                        width="320"
                                        isWidthInPercentage={false}
                                        items={filteredData}
                                        callback={handleChooseTemplate}
                                        searchable={true}
                                    />
                                </div>
                                {selectedTemplate && (
                                    <button
                                        type="button"
                                        id="download-sample-csv-btn-id"
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline cursor-pointer border-0 bg-transparent p-0 self-start sm:self-auto"
                                        onClick={downloadCsv}
                                    >
                                        <Download size={14} />
                                        {content.ADD_EXPERIENCE_MODAL.add_experience_modal.download_sample_file}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <label
                                    htmlFor="upload-csv-file-id"
                                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-semibold shadow-xs transition-colors cursor-pointer ${
                                        loading || validateCsvLoading || !selectedTemplate
                                            ? "opacity-50 cursor-not-allowed text-stride-muted border-border bg-surface-subtle"
                                            : "border-border-subtle bg-white text-stride-body hover:bg-surface hover:text-primary hover:border-primary/40"
                                    }`}
                                >
                                    <Upload size={14} />
                                    <span>Choose CSV File</span>
                                </label>
                                <input
                                    disabled={
                                        loading ||
                                        validateCsvLoading ||
                                        !selectedTemplate
                                    }
                                    id='upload-csv-file-id'
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleSelectCSV}
                                    accept=".csv"
                                    className="sr-only"
                                    onClick={(e) => {
                                        e.target.value = null;

                                        // Clear table & related states
                                        setRowData([]);
                                        setErrors({});
                                        setCsvValidation(CSV_DATA_STATUS);
                                        setError("");
                                    }}
                                />
                                <span className="text-xs text-stride-muted truncate max-w-xs">
                                    {fileInputRef.current?.files?.[0]?.name || "No file chosen (.csv format)"}
                                </span>
                            </div>

                            {(csvValidation.success || csvValidation.error || error) && (
                                <div className="space-y-1">
                                    {csvValidation.success && !error && !csvValidation.error && (
                                        <p className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200/60 inline-block">
                                            {csvValidation.success}
                                        </p>
                                    )}
                                    {csvValidation.error && (
                                        <p className="text-xs font-medium text-danger bg-danger-soft px-3 py-1.5 rounded-lg border border-danger/20 block">
                                            {csvValidation.error}
                                        </p>
                                    )}
                                    {error && (
                                        <p className="text-xs font-medium text-danger bg-danger-soft px-3 py-1.5 rounded-lg border border-danger/20 block">
                                            {error}
                                        </p>
                                    )}
                                </div>
                            )}

                            {rowData.length > 0 && (
                                <div className="ag-theme-quartz border border-border rounded-xl overflow-hidden shadow-xs" style={{ height: "350px", width: "100%" }}>
                                    <div
                                        id="experience-preview-table-id"
                                        className="w-full h-full"
                                    >
                                        <AgGridWrapper
                                            rowData={rowData}
                                            columnDefs={columnDefs}
                                            autoSizeStrategy={autoSizeStrategy}
                                            rowSelection="multiple"
                                            suppressRowClickSelection
                                            onRowSelected={handleOnRowSelected}
                                            onGridReady={onGridReady}
                                            onSortChanged={onSortChanged}
                                            onCellValueChanged={onCellValueChanged}
                                            onCellClicked={onCellClicked}
                                            onCellEditingStopped={onCellEditingStopped}
                                            stopEditingWhenCellsLoseFocus={true}
                                        />
                                    </div>

                                    {isComponentVisible && (
                                        <div 
                                            ref={ref}
                                            className="fixed z-50 w-44 rounded-xl bg-white shadow-xl border border-border p-1.5 focus:outline-none"
                                            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left - 150}px` }}
                                        >
                                            <button
                                                type="button"
                                                id="add-duplicate-of-first-row-btn-id"
                                                onClick={handleAddDuplicateRow}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-stride-body hover:bg-surface-alt hover:text-stride-heading rounded-lg transition-colors text-left border-0 bg-transparent cursor-pointer"
                                            >
                                                <Copy size={13} className="text-stride-muted" />
                                                {content.ADD_EXPERIENCE_MODAL.add_experience_modal.duplicate_btn}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleAddRow}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-stride-body hover:bg-surface-alt hover:text-stride-heading rounded-lg transition-colors text-left border-0 bg-transparent cursor-pointer"
                                            >
                                                <Plus size={13} className="text-stride-muted" />
                                                {content.ADD_EXPERIENCE_MODAL.add_experience_modal.add_new_row_btn}
                                            </button>
                                            {selectedRowData.rowIndex !== 0 && (
                                                <>
                                                    <div className="h-px bg-border-subtle my-1" />
                                                    <button
                                                        type="button"
                                                        onClick={handleDeleteRow}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-danger hover:bg-danger-soft rounded-lg transition-colors text-left border-0 bg-transparent cursor-pointer"
                                                    >
                                                        <Trash2 size={13} className="text-danger" />
                                                        {content.ADD_EXPERIENCE_MODAL.add_experience_modal.delete_btn}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    <div className="p-2.5 bg-surface-subtle border-t border-border">
                                        <p className="text-xs text-stride-muted italic m-0">
                                            {content.ADD_EXPERIENCE_MODAL.add_experience_modal.note}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {selectedCellToEdit && (
                                <ManageEditInputModal
                                    element={selectedCellToEdit}
                                    showEditor={showEditor}
                                    handleSave={handleModalSave}
                                    handleHideModal={handleHideEditModal}
                                />
                            )}

                            <div className="flex items-center justify-between border-t border-border pt-4 mt-6">
                                <div>
                                    {deleteIds.length > 0 && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDeleteRow(deleteIds)}
                                            icon={<Trash2 size={14} />}
                                        >
                                            {content.ADD_EXPERIENCE_MODAL.add_experience_modal.delete_btn} ({deleteIds.length})
                                        </Button>
                                    )}
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleClose(false)}
                                    >
                                        {content.ADD_EXPERIENCE_MODAL.add_experience_modal.cancel_btn}
                                    </Button>
                                    <Button
                                        id={"submit-btn-id"}
                                        variant="primary"
                                        size="sm"
                                        disabled={
                                            loading || 
                                            validateCsvLoading || 
                                            !!csvValidation.error || 
                                            !selectedTemplate ||
                                            rowData.length <= 1 ||
                                            (usageCount >= 0 && rowData.length - 1 > usageCount)
                                        }
                                        onClick={handleSubmit}
                                        loading={loading || validateCsvLoading}
                                    >
                                        {content.ADD_EXPERIENCE_MODAL.add_experience_modal.add_btn}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </PrimaryModal>
            )}
        </>
    );
};

export default AddExperienceModal;